import React, { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { FiKey } from "react-icons/fi";
import Button from "components/atoms/Button";
import InputField from "components/atoms/InputField";
import InputSelect from "components/atoms/InputSelect";
import { MEDICATION_KEYS, toOption } from "pages/MasterPengobatanStandar/constants";
import { errorMessages } from "services/StandardTreatment";

export const DRUG_MODAL_ID = "drugFormModal";

const KEY_OPTIONS = MEDICATION_KEYS.map((key) => ({ label: key, value: key }));
const STATUS_OPTIONS = [
  { label: "Aktif", value: true },
  { label: "Nonaktif", value: false },
];
const EMPTY_DRUG = { code: "", name: "", dosage_form: "", medication_key: "", is_active: true };
const TITLES = { add: "Tambah Obat", edit: "Edit Obat", detail: "Detail Obat" };

// Body mengikuti DrugWrite (openapi.yaml): dosage_form & medication_key nullable.
const toBody = (v) => ({
  code: v.code.trim(),
  name: v.name.trim(),
  dosage_form: v.dosage_form.trim() || null,
  medication_key: v.medication_key || null,
  is_active: v.is_active,
});

// Pola modal mengikuti DataDasarPenduduk/ModalDesaForm (Bootstrap modal + Formik).
const DrugFormModal = ({ mode, drug, onSubmit, onEdit }) => {
  const [serverErrors, setServerErrors] = useState([]);
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: drug
      ? { ...EMPTY_DRUG, ...drug, dosage_form: drug.dosage_form || "", medication_key: drug.medication_key || "" }
      : EMPTY_DRUG,
    validationSchema: Yup.object({
      code: Yup.string().trim().max(50).required("Kode wajib diisi"),
      name: Yup.string().trim().max(150).required("Nama obat wajib diisi"),
      dosage_form: Yup.string().max(100),
      medication_key: Yup.string().oneOf(["", ...MEDICATION_KEYS], "Medication key tidak dikenali"),
    }),
    onSubmit: (values) => {
      setServerErrors([]);
      return onSubmit(toBody(values)).catch((err) => setServerErrors(errorMessages(err)));
    },
  });

  const errorOf = (key) => formik.touched[key] && formik.errors[key];
  const fieldProps = (key) => ({ ...formik.getFieldProps(key), error: errorOf(key), errorMessage: formik.errors[key] });

  return (
    <div className="modal fade md-modal" id={DRUG_MODAL_ID} tabIndex={-1} aria-labelledby={`${DRUG_MODAL_ID}Label`} aria-hidden="true">
      <div className="modal-dialog">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title" id={`${DRUG_MODAL_ID}Label`}>
              {TITLES[mode]}
            </h5>
            <button type="button" className="close" data-dismiss="modal" aria-label="Tutup">
              <span aria-hidden="true">×</span>
            </button>
          </div>

          {mode === "detail" && drug ? (
            <>
              <div className="modal-body">
                <dl className="md-detail">
                  <dt>Kode</dt>
                  <dd>{drug.code}</dd>
                  <dt>Nama obat</dt>
                  <dd>{drug.name}</dd>
                  <dt>Bentuk sediaan</dt>
                  <dd>{drug.dosage_form || "—"}</dd>
                  <dt>Medication key</dt>
                  <dd>
                    {drug.medication_key ? (
                      <code className="md-code">{drug.medication_key}</code>
                    ) : (
                      <span className="mps-pill is-warning">Belum dipetakan</span>
                    )}
                  </dd>
                  <dt>Status</dt>
                  <dd>
                    <span className={`mps-pill ${drug.is_active ? "is-success" : "is-neutral"}`}>
                      {drug.is_active ? "Aktif" : "Nonaktif"}
                    </span>
                  </dd>
                </dl>
              </div>
              <div className="modal-footer">
                <Button type="button" variant="secondary" size="normal" data-dismiss="modal">
                  Tutup
                </Button>
                <Button type="button" variant="primary" size="normal" onClick={onEdit}>
                  Edit
                </Button>
              </div>
            </>
          ) : (
            <form onSubmit={formik.handleSubmit} noValidate>
              <div className="modal-body">
                {serverErrors.length > 0 && (
                  <div className="mps-alert is-error" role="alert">
                    <ul className="mps-alert-list mt-0">
                      {serverErrors.map((m) => (
                        <li key={m}>{m}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <div className="row">
                  <div className="col-sm-5 md-field">
                    <InputField label="Kode" isRequired placeholder="Contoh: PQ" {...fieldProps("code")} />
                  </div>
                  <div className="col-sm-7 md-field">
                    <InputField label="Nama obat" isRequired placeholder="Contoh: Primaquine" {...fieldProps("name")} />
                  </div>
                  <div className="col-12 md-field">
                    <InputField label="Bentuk sediaan" placeholder="Contoh: Tablet 15 mg" {...fieldProps("dosage_form")} />
                  </div>
                  <div className="col-12 md-field">
                    <div className="md-highlight-box">
                      <InputSelect
                        label="Medication key"
                        placeholder="Pilih medication key"
                        data={KEY_OPTIONS}
                        value={toOption(KEY_OPTIONS, formik.values.medication_key)}
                        onChange={(sel) => formik.setFieldValue("medication_key", sel?.value ?? "")}
                        error={errorOf("medication_key")}
                        errorMessage={formik.errors.medication_key}
                      />
                      <p className="md-help">
                        <FiKey size={14} />
                        Kunci di <code>medicationGiven</code> yang dipakai treatment evaluation untuk
                        mencocokkan obat yang diberikan. Hanya 11 key ini yang dikenali backend.
                      </p>
                    </div>
                    {!formik.values.medication_key && (
                      <p className="md-warning mt-2">
                        Tanpa medication key, obat ini tidak pernah cocok dan versi yang memakainya
                        ditolak saat rilis (DRUG_HAS_MEDICATION_KEY).
                      </p>
                    )}
                  </div>
                  <div className="col-sm-6 md-field">
                    <InputSelect
                      label="Status"
                      isClearable={false}
                      data={STATUS_OPTIONS}
                      value={toOption(STATUS_OPTIONS, formik.values.is_active)}
                      onChange={(sel) => formik.setFieldValue("is_active", sel?.value ?? true)}
                    />
                  </div>
                </div>
                {!formik.values.is_active && (
                  <p className="md-warning">
                    Obat nonaktif tidak ditawarkan pada Terapi, dan versi yang masih memakainya ditolak
                    saat rilis (REFERENCED_MASTER_ACTIVE). Obat tidak pernah dihapus.
                  </p>
                )}
              </div>
              <div className="modal-footer">
                <Button type="button" variant="secondary" size="normal" data-dismiss="modal">
                  Batal
                </Button>
                <Button type="submit" variant="primary" size="normal" isDisabled={formik.isSubmitting}>
                  {formik.isSubmitting ? "Menyimpan..." : "Simpan"}
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default DrugFormModal;
